// © Microsoft Corporation. All rights reserved.

/** The AppType enum. */
export enum AppType {
    MMX,
    Edge,
    EdgeChromium,
    FirstParty,
    MicrosoftNews,
    Office,
    Hub,
    Unknown,
    Views,
    SuperApp,
    Windows,
    Homepage,
    WindowsShell,
    NewsAndInterests,
    WindowsNewsPlus
}

/** The AudienceMode enum. */
export enum AudienceModeType {
    Enterprise = "enterprise"
}

/** The PageType enum */
export enum PageType {
    MMX,
    DHP,
    NTP,
    WindowsHP,
    NewsBar,
    Unknown
}

/** The WebWorkerMessageId enum. */
export enum WebWorkerMessageId {
    AppAnonCookie = "appanoncookie",
    MyFeed = "myfeed",
    AuthToken = "AuthToken",
    UpdateMyFeed = "updatemyfeed",
    RefreshConfigs = "refreshconfigs",
    DetectConfigChanges = "detectconfigchanges",
    ClearConfigCache = "clearconfigcache",
    Updatepdp = "updatepdp",
    WeatherPdp = "weatherpdp",
    MoneyPdp = "moneypdp",
    LocalNews = "localnews",
    SportsTeamPdp = "sportsteampdp",
    SportsPlayerPdp = "sportsplayerpdp",
    SportsLeaguePdp = "sportsleaguepdp",
    PerfMarkers = "perfmarkers",
    ContentPreviewData = "ContentPreviewData",
    ContentPreviewCache = "ContentPreviewCache",
    PostTtvr = "PostTtvr",
    FeedDisplaySetting = "FeedDisplaySetting",
    WebWorkerError = "WebWorkerError",
    InvalidateWeatherPdp = "InvalidateWeatherPdp",
    StripeMyFeed = "StripeMyFeed",
    UpdateMyBriefing = "UpdateMyBriefing",
    MyBriefing = "MyBriefing",
    WindowsFeed = "WindowsFeed",
    ViewsData = "ViewsData",
    ViewsDataFetch = "ViewsDataFetch",
    IndustryNewsArticles = "IndustryNewsArticles"
}

/** The AllEnvironments interface. */
export interface AllEnvironments {
    /** The production environment */
    prod: {
        /** The domain URL */
        domain: string;
    };

    /** The int environment */
    int: {
        /** The domain URL */
        domain: string;
    };

    /** The int environment pointing to PCS pull request build instances */
    intPR: {
        /** The domain URL */
        domain: string;
    };

    /** The int environment pointing to PCS perf build instances */
    intPef: {
        /** The domain URL */
        domain: string;
    };

    /** The int1 environment */
    int1: {
        /** The domain URL */
        domain: string;
    }
}

/** The FeatureFlags interface. */
export interface FeatureFlags {

    /** PCS flag to change config service depth param */
    crsMaxDepth: string;

    /** PCS flag to disable sending the activityID in the request header instead of sending it as a QSP in the URL. */
    disableActivityIdInHeader: string;

    /** The flag to disable config caching. */
    disableCachingConfigs: string

    /** The flag to turn off content preview caching */
    disableContentPreviewCache: string;

    /** The flag to disable my feed fetch call by web worker. */
    disableMyFeedFetch: string

    /** The flag to turn on/off config service. */
    enableConfigService: string

    /** PCS flag to turn off getting multiple configs in CRS */
    isMultiConfigRetrievalEnabled: string;
}

/** The all environments. */
export const allEnvironments: AllEnvironments = {
    prod: {
        domain: "https://www.msn.com"
    },
    int1: {
        domain: "https://int1.msn.com"
    },
    int: {
        domain: "https://int.msn.com"
    },
    intPR: {
        domain: "https://int.msn.com"
    },
    intPef: {
        domain: "https://int.msn.com"
    }
};

/** The Experience Types enum */
export enum ExperienceTypes {
    ContentPreview = "contentpreview",
    River = "river",
    EntryPointEdgeNext = "entrypointedgenext",
    EntryPoint = "entrypoint",
    IndustryNewsCard = "industrynewscard",
    PlacementManager = "placementmanager",
    WeatherCard = "weathercard",
    MyBriefing = "mybriefrivercard",
    NewsbarFeed = "newsbarfeed",
    DesktopFeed = "desktopfeed",
    DesktopFeedEdgeNext = "desktopfeededgenext",
    DesktopFeedSingleColumn = "desktopfeedsinglecolumn"
}

export const ocidMapping = {
    [PageType.MMX]: "EMMX",
    [PageType.DHP]: "spartan-dhp-feeds",
    [PageType.NTP]: "spartan-ntp-feeds"
};

export const scnMapping = {
    [PageType.MMX]: "APP_ANON",
    [PageType.DHP]: "APP_ANON",
    [PageType.NTP]: "APP_ANON",
    [PageType.WindowsHP]: "ANON",
    [PageType.NewsBar]: "APP_ANON"
};

export interface Dictionary<T> {
    [index: string]: T;
}

export enum RiverConfigType {
    MyFeed = "MyFeed",
    StripeMyFeed = "StripeMyFeed"
}

export const enum FeedDisplayMode4EdgeChromium {
    always = "always"
}

/** The Web Worker message interface. */
export interface Message {

    /** The message id. */
    id: WebWorkerMessageId;

    /** The type of message. */
    type?: string;

    /** The additional message payload */
    payload?: unknown;
}

export interface WorkerMessageEvent extends MessageEvent {
    /** Message data */
    data: Message;
}

export interface WebWorkerResponse {
    /** The message id. */
    id: WebWorkerMessageId | string;

    /** The item fetched status. */
    fetched: boolean;

    /** The item payload. */
    payload: any
}

export enum MessageType {
    Data = "data",
    Command = "command",
    Subscription = "subscription"
}

export interface WebWorkerCacheItem {
    /** payload to add */
    payload: any;
}
